import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';

/**
 *  LEMORZSOLÓDÁSSAL VESZÉLYEZTETETT TANULÓK (backend/lemorzsolodas.js; 229/2012. Korm. r. 26/A. §, 44/J. §).
 *
 *  Két helyen fut ugyanez a komponens:
 *    - mod = 'admin': Admin › Napló és értékelés › Ellenőrzés – az intézmény minden 5–12. évfolyamos
 *      osztálya, osztályszűrővel, az összesített adatszolgáltatás (PDF) letöltésével és a határidővel;
 *    - mod = 'of': az Osztályfőnök oldal „Lemorzsolódás” nézete – csak a kiválasztott (saját) osztály.
 *  A jogot a kiszolgáló dönti el; az intézkedést az osztályfőnök a saját tanulójánál rögzítheti.
 */
interface LmKriterium { kod: string; pont: string; nev: string; rovid: string; leiras: string; tantargyas?: boolean; }
interface LmErtek { teljesul: boolean; varhato?: boolean; szoveg?: string; tantargyak?: { id: string; nev: string }[]; feltetelek?: Record<string, boolean>; }
interface LmIntezkedes { id: number; tipus: string; tantargy?: string | null; datum?: string | null; megjegyzes?: string | null; rogzitette?: string | null; rogzitve?: string; }
interface LmTanulo {
  oktazon: string; nev: string; osztaly_id: string; osztaly: string; evfolyam: number; fh_kod: string;
  veszelyeztetett: boolean; csak_varhato: boolean; hivatalos: boolean;
  kriteriumok: Record<string, LmErtek>;
  mulasztas: { igazolt: number; igazolatlan: number; elbiralatlan: number };
  targyak: { id: string; nev: string; jegy: number | null; evkoziAtlag?: number; jegyDb?: number; szoveg?: string | null }[];
  intezkedesek: LmIntezkedes[];
  figyelmeztetesek: string[];
}
interface LmValasz {
  tanev: { ev: number; nev: string; tankezd: string; felev: string; tanvege: string };
  idoszak: { felev: number; tol: string; ig: string; hatarido: string; mulasztas_ig: string; lezart: boolean };
  kriteriumok: LmKriterium[];
  k_feltetelek: { kod: string; pont: string; nev: string }[];
  tanulok: LmTanulo[];
  osztalyok?: { id: string; nev: string; evfolyam: number }[];
  hatarido?: { ev: number; felev: number; hatarido: string; napok: number; emlekezteto: boolean } | null;
  megjegyzes?: string;
  jogalap: string;
  jog?: { admin: boolean; intezkedes: boolean };
}

@Component({
  selector: 'app-lemorzsolodas',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './lemorzsolodas.component.html',
  styleUrls: ['./lemorzsolodas.component.scss'],
})
export class LemorzsolodasComponent implements OnInit, OnChanges {
  /** 'admin' (route-ként) vagy 'of' (az osztályfőnöki oldalba ágyazva). */
  @Input() mod: 'admin' | 'of' = 'admin';
  @Input() osztaly = '';
  @Input() intezmeny = '';

  betolt = false;
  hiba = '';
  adat: LmValasz | null = null;
  felev: number | null = null;
  szuroOsztaly = '';
  csakVeszelyeztetett = true;
  varhatoRiportban = false;
  nyitva: Record<string, boolean> = {};
  intezkedesTipusok: { kod: string; nev: string }[] = [];
  uj: Record<string, { tipus: string; tantargy: string; datum: string; megjegyzes: string }> = {};
  mentes = false;
  letoltes = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit(): Promise<void> {
    if (!this.intezmeny) {
      const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
      this.intezmeny = String(sajat?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    }
    const t: any = await firstValueFrom(this.http.get('/api/lemorzsolodas/tipusok')).catch(() => null);
    this.intezkedesTipusok = t?.intezkedesek || [];
    await this.toltes();
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['osztaly'] && !ch['osztaly'].firstChange && this.mod === 'of') this.toltes();
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  async toltes(): Promise<void> {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    if (this.mod === 'of' && !this.osztaly) return;
    this.betolt = true; this.hiba = '';
    try {
      const params: any = {};
      if (this.felev) params.felev = this.felev;
      if (this.adat?.tanev?.ev) params.ev = this.adat.tanev.ev;
      let url: string;
      if (this.mod === 'of') url = `/api/lemorzsolodas/osztaly/${encodeURIComponent(this.osztaly)}/${this.intezmenyPath}`;
      else { url = `/api/lemorzsolodas/kimutatas/${this.intezmenyPath}`; if (this.szuroOsztaly) params.osztaly = this.szuroOsztaly; }
      const valasz = await firstValueFrom(this.http.get<LmValasz>(url, { params }));
      //  Az osztálylista a szűretlen válaszból marad meg (szűrve csak egy osztály jön vissza).
      if (this.adat?.osztalyok && this.szuroOsztaly && this.mod === 'admin') valasz.osztalyok = this.adat.osztalyok;
      this.adat = valasz;
      this.felev = valasz.idoszak?.felev || this.felev;
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a kimutatást.';
    } finally { this.betolt = false; }
  }

  felevValt(f: any): void { this.felev = Number(f) || 1; this.toltes(); }
  osztalyValt(): void { this.toltes(); }

  get kriteriumok(): LmKriterium[] { return this.adat?.kriteriumok || []; }
  get sorok(): LmTanulo[] { return (this.adat?.tanulok || []).filter((t) => !this.csakVeszelyeztetett || t.veszelyeztetett); }
  get veszelyeztetettDb(): number { return (this.adat?.tanulok || []).filter((t) => t.veszelyeztetett).length; }
  get csakVarhatoDb(): number { return (this.adat?.tanulok || []).filter((t) => t.csak_varhato).length; }
  get zarojegyNelkulDb(): number { return (this.adat?.tanulok || []).filter((t) => !t.hivatalos).length; }
  pontDb(kod: string): number { return (this.adat?.tanulok || []).filter((t) => t.kriteriumok?.[kod]?.teljesul).length; }

  teljesul(t: LmTanulo, kod: string): boolean { return !!t.kriteriumok?.[kod]?.teljesul; }
  varhato(t: LmTanulo, kod: string): boolean { return !!t.kriteriumok?.[kod]?.varhato; }
  cimke(t: LmTanulo, k: LmKriterium): string {
    const x = t.kriteriumok?.[k.kod];
    return `${k.pont} – ${k.nev}${x?.szoveg ? ': ' + x.szoveg : ''}${x?.varhato ? ' (várható, az évközi jegyekből)' : ''}`;
  }
  teljesultek(t: LmTanulo): LmKriterium[] { return this.kriteriumok.filter((k) => this.teljesul(t, k.kod)); }
  tipusNev(kod: string): string { return this.intezkedesTipusok.find((x) => x.kod === kod)?.nev || kod; }
  targyNev(t: LmTanulo, id?: string | null): string { return id ? (t.targyak.find((x) => x.id === id)?.nev || '') : ''; }
  jegySzoveg(j: { jegy: number | null; evkoziAtlag?: number; szoveg?: string | null }): string {
    if (j.jegy === null || j.jegy === undefined) return j.szoveg ? 'szöveges' : '–';
    return j.evkoziAtlag !== undefined ? `${j.jegy} (évközi átlag ${String(j.evkoziAtlag).replace('.', ',')})` : String(j.jegy);
  }

  valt(t: LmTanulo): void {
    this.nyitva[t.oktazon] = !this.nyitva[t.oktazon];
    if (!this.uj[t.oktazon]) this.uj[t.oktazon] = { tipus: '', tantargy: '', datum: new Date().toLocaleDateString('sv-SE'), megjegyzes: '' };
  }

  async intezkedesMent(t: LmTanulo): Promise<void> {
    const u = this.uj[t.oktazon];
    if (!u?.tipus || !this.adat) { this.data.sendToast('Válaszd ki az intézkedés fajtáját.', 'warning'); return; }
    this.mentes = true;
    try {
      await firstValueFrom(this.http.post(`/api/lemorzsolodas/intezkedes/${this.intezmenyPath}`, {
        oktazon: t.oktazon, ev: this.adat.tanev.ev, felev: this.adat.idoszak.felev,
        tipus: u.tipus, tantargy: u.tantargy || null, datum: u.datum || null, megjegyzes: u.megjegyzes || null,
      }));
      this.data.sendToast('Intézkedés rögzítve.', 'success');
      this.uj[t.oktazon] = { tipus: '', tantargy: '', datum: u.datum, megjegyzes: '' };
      await this.toltes();
      this.nyitva[t.oktazon] = true;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A rögzítés nem sikerült.', 'error');
    } finally { this.mentes = false; }
  }

  async intezkedesTorol(t: LmTanulo, x: LmIntezkedes): Promise<void> {
    if (!confirm(`Törlöd az intézkedést?\n\n${this.tipusNev(x.tipus)}${x.megjegyzes ? ' – ' + x.megjegyzes : ''}`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/lemorzsolodas/intezkedes/${x.id}/${this.intezmenyPath}`));
      await this.toltes();
      this.nyitva[t.oktazon] = true;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }

  /** Az összesített adatszolgáltatás PDF-ben (névtelen, 26/A. § (2)) – csak admin. */
  async riport(): Promise<void> {
    if (!this.adat) return;
    this.letoltes = true;
    try {
      const params: any = { formatum: 'pdf', ev: this.adat.tanev.ev, felev: this.adat.idoszak.felev };
      if (this.varhatoRiportban) params.varhato = '1';
      const blob = await firstValueFrom(this.http.get(`/api/lemorzsolodas/riport/${this.intezmenyPath}`, { params, responseType: 'blob' }));
      this.ment(blob, `lemorzsolodas-${this.adat.tanev.ev}-${this.adat.idoszak.felev === 1 ? 'I' : 'II'}-felev.pdf`);
    } catch (e: any) {
      this.data.sendToast('A riport most nem készíthető el.', 'error');
    } finally { this.letoltes = false; }
  }

  csv(): void {
    if (!this.adat) return;
    const cella = (v: any) => { const s = String(v ?? ''); return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const fej = ['Tanuló', 'Oktatási azonosító', 'Osztály', 'Évfolyam', 'Veszélyeztetett',
      ...this.kriteriumok.map((k) => `${k.kod}) ${k.rovid}`), 'Igazolt óra', 'Igazolatlan óra', 'Elbírálatlan óra', 'Intézkedések', 'Részletek'];
    const ki = [fej.join(';')];
    for (const t of this.sorok) {
      ki.push([t.nev, t.oktazon, t.osztaly, t.evfolyam, t.veszelyeztetett ? 'igen' : 'nem',
        ...this.kriteriumok.map((k) => (this.teljesul(t, k.kod) ? (this.varhato(t, k.kod) ? 'várható' : 'igen') : '')),
        t.mulasztas.igazolt, t.mulasztas.igazolatlan, t.mulasztas.elbiralatlan,
        t.intezkedesek.map((x) => this.tipusNev(x.tipus)).join(', '),
        this.teljesultek(t).map((k) => `${k.kod}) ${t.kriteriumok[k.kod]?.szoveg || ''}`).join(' | ')].map(cella).join(';'));
    }
    const blob = new Blob([`﻿${ki.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
    this.ment(blob, `lemorzsolodas-${this.adat.tanev.ev}-${this.adat.idoszak.felev}${this.mod === 'of' ? '-osztaly' : ''}.csv`);
  }

  private ment(blob: Blob, nev: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = nev;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /** ÉÉÉÉ-HH-NN → ÉÉÉÉ. HH. NN. */
  datum(d?: string | null): string { return d ? `${d.slice(0, 4)}. ${d.slice(5, 7)}. ${d.slice(8, 10)}.` : ''; }
}
